import {
    berekenAantalBussen,
    berekenBusFrequentie,
    berekenBussenVerdeling,
    PeriodeFactor,
    Reisperiode,
    Rijtijd,
    TvvBesteldeRoute,
} from "@/entities/tvv";
import { EditBestellingData } from "@/features/tvv/components/regievoerder/bestelling-bewerken/schema.ts";
import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";

type UseBestellingFormHandlersParams = {
    form: UseFormReturn<EditBestellingData>;
    route: TvvBesteldeRoute;
};

export function useBestellingFormHandlers({ form, route }: UseBestellingFormHandlersParams) {
    const rijtijd = Rijtijd.getTotaleRijtijd(route.rijtijden) ?? -1;
    const currentReisperiode = form.watch("reisperiode");

    const getBussenBeginStation = useCallback(
        (newAantalBussen: number): number | undefined => {
            const currentPeriodeFactor = PeriodeFactor.getPeriodeFactor(route.standaardRoute?.periodeFactoren ?? [], currentReisperiode);
            if (!currentPeriodeFactor) return undefined;
            const percentage = Math.round(currentPeriodeFactor.deelBussenBeginStation * 100);
            const { bussenBeginStation } = berekenBussenVerdeling(newAantalBussen, percentage);
            return bussenBeginStation;
        },
        [currentReisperiode, route.standaardRoute?.periodeFactoren]
    );

    const handleFrequentieChange = useCallback(
        (newFrequentie: number) => {
            // busFrequentie is already set by field.onChange, compute derived values.
            // Zonder bekende rijtijd is frequentie <-> aantalBussen niet te berekenen; laat de velden los.
            form.setValue("isAangepast", true);
            if (rijtijd <= 0) return;
            const newAantalBussen = berekenAantalBussen(newFrequentie, rijtijd);
            form.setValue("aantalBussen", newAantalBussen);
            const bussenBeginStation = getBussenBeginStation(newAantalBussen);
            if (bussenBeginStation !== undefined) {
                form.setValue("aantalBussenOpBeginStation", bussenBeginStation);
            }
        },
        [form, getBussenBeginStation, rijtijd]
    );

    const handleAantalBussenChange = useCallback(
        (newAantal: number) => {
            // aantalBussen is already set by field.onChange, compute derived values.
            // Zonder bekende rijtijd is aantalBussen <-> frequentie niet te berekenen; laat de velden los.
            form.setValue("isAangepast", true);
            if (rijtijd <= 0) return;
            form.setValue("busFrequentie", berekenBusFrequentie(newAantal, rijtijd));
            const bussenBeginStation = getBussenBeginStation(newAantal);
            if (bussenBeginStation !== undefined) {
                form.setValue("aantalBussenOpBeginStation", bussenBeginStation);
            }
        },
        [form, getBussenBeginStation, rijtijd]
    );

    const handleReisperiodeChange = useCallback(
        (value: string) => {
            // reisperiode is already set by field.onChange, reset to initial values for new period
            const newReisperiode = value as Reisperiode;
            const periodeFactor = PeriodeFactor.getPeriodeFactor(route.standaardRoute?.periodeFactoren ?? [], newReisperiode);

            if (periodeFactor) {
                const initWaarden = PeriodeFactor.getInitieleBestelWaarden(periodeFactor, rijtijd);
                form.setValue("busFrequentie", initWaarden.busFrequentie);
                form.setValue("aantalBussen", initWaarden.aantalBussen);
                form.setValue("aantalBussenOpBeginStation", initWaarden.bussenBeginStation);
                form.setValue("isAangepast", false);
            }
        },
        [form, rijtijd, route]
    );

    return {
        handleFrequentieChange,
        handleAantalBussenChange,
        handleReisperiodeChange,
    };
}
